import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AuthCardView } from './auth-card.types';
import {
  AUTH_CALLBACK_PATH,
  authEnabled,
  useClerk,
  useSignIn,
  useSignUp,
} from '../../lib/auth';
import { getClerkErrorMessage, getClerkErrorCode, AUTH_DISABLED_MESSAGE, DEFAULT_AFTER_AUTH_URL } from './auth-card.utils';
import { log } from '@/lib/logger';
import {
  beginDashboardAuthAttempt, clearDashboardAuthAttempt, expectDashboardAuthCompletion,
  reportDashboardAuthOutcome, reportDashboardAuthVerification,
} from '../../lib/dashboardAuthTelemetry';

function isAlreadySignedInError(error: unknown): boolean {
  const msg = getClerkErrorMessage(error).toLowerCase();
  return msg.includes('already signed in') || msg.includes('session already exists');
}

interface UseAuthCardSubmitOptions {
  afterAuthUrl: string;
  initialView?: 'main' | 'forgot-password';
}

/* eslint-disable @typescript-eslint/no-explicit-any */

export function useAuthCardSubmit({ afterAuthUrl, initialView = 'main' }: UseAuthCardSubmitOptions) {
  const [view, setView] = useState<AuthCardView>(initialView);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const navigate = useNavigate();

  const clerk = useClerk();
  const { isLoaded: isSignInLoaded, signIn, setActive } = useSignIn();
  const { isLoaded: isSignUpLoaded, signUp } = useSignUp();

  const isCardReady = !authEnabled || (isSignInLoaded && isSignUpLoaded);
  const isInteractionDisabled = isSubmitting || !isCardReady || !authEnabled;

  // Google OAuth — always use signIn flow; Clerk auto-creates accounts for
  // unknown OAuth emails via `transferable` (default true). AuthCallbackPage
  // handles legal_accepted missing requirement.
  const handleGoogleAuth = useCallback(async () => {
    log.info('[useAuthCardSubmit] handleGoogleAuth');
    setFormError(null);
    if (!authEnabled) { setFormError(AUTH_DISABLED_MESSAGE); return; }
    beginDashboardAuthAttempt('google');

    try {
      setIsSubmitting(true);

      const params = new URLSearchParams(window.location.search);
      if (params.has('signedOut') && clerk.client) {
        log.info('[AuthCard] Post-sign-out detected, clearing residual Clerk sessions');
        await clerk.signOut();
      }

      // Pass the intended redirect through the callback URL so AuthCallbackPage
      // can navigate there after OAuth completes (instead of hardcoding /dashboard)
      const callbackUrl = afterAuthUrl && afterAuthUrl !== '/dashboard'
        ? `${AUTH_CALLBACK_PATH}?redirect_url=${encodeURIComponent(afterAuthUrl)}`
        : AUTH_CALLBACK_PATH;

      if (!signIn) throw new Error('Sign-in is still loading. Try again.');
      expectDashboardAuthCompletion(null);
      reportDashboardAuthOutcome('redirect_started', 'oauth');
      await signIn.authenticateWithRedirect({
        redirectUrl: callbackUrl,
        redirectUrlComplete: afterAuthUrl,
        strategy: 'oauth_google',
      });
    } catch (error) {
      if (isAlreadySignedInError(error)) {
        reportDashboardAuthOutcome('incomplete', 'sign_in');
        log.info('[useAuthCardSubmit] Already signed in, redirecting to dashboard');
        navigate(afterAuthUrl || DEFAULT_AFTER_AUTH_URL, { replace: true });
        return;
      }
      reportDashboardAuthOutcome('failed', 'oauth', getClerkErrorCode(error));
      setFormError(getClerkErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }, [afterAuthUrl, signIn, clerk, navigate]);

  // Unified email + password: try sign-in first, fall back to sign-up
  const handleEmailPasswordSubmit = useCallback(async (email: string, password: string) => {
    log.info('[useAuthCardSubmit] handleEmailPasswordSubmit (unified)');
    setFormError(null);
    if (!authEnabled) { setFormError(AUTH_DISABLED_MESSAGE); return; }
    beginDashboardAuthAttempt('email_password');

    try {
      setIsSubmitting(true);

      // Step 1: Try signing in
      if (!signIn) throw new Error('Sign-in is still loading.');
      const result: any = await signIn.create({
        identifier: email,
        password,
        strategy: 'password',
      } as any);
      log.info('[useAuthCardSubmit] signIn.create result status:', { status: result.status });
      if (result.status === 'complete' && result.createdSessionId && setActive) {
        expectDashboardAuthCompletion(result.createdSessionId);
        await setActive({ session: result.createdSessionId });
        navigate(afterAuthUrl || DEFAULT_AFTER_AUTH_URL, { replace: true });
        return;
      }
      // Clerk wants a second factor (e.g. email-code MFA). Prepare the code and
      // route to the two-factor view so the user can enter it in the form.
      if (result.status === 'needs_second_factor') {
        reportDashboardAuthOutcome('verification_required', 'second_factor');
        const factor = result.supportedSecondFactors?.find(
          (f: any) => f.strategy === 'email_code'
        );
        // A prepare failure must NOT skip the view switch. `signIn.create` has
        // already LATCHED Clerk in needs_second_factor, so the first-factor form
        // will no longer accept this password: letting the throw reach the outer
        // catch leaves the user on a dead-end form with no route to the code view
        // and no resend control (this is the only prepareSecondFactor call site).
        // The two-factor view renders formError and has a Back button, so landing
        // there always wins: the reason is visible, a code delivered by an earlier
        // attempt still works, and Back re-opens the retry.
        let prepareError: unknown = null;
        if (factor && result.prepareSecondFactor) {
          try {
            await result.prepareSecondFactor({ strategy: 'email_code', emailAddressId: factor.emailAddressId });
          } catch (e) {
            // Transient by nature: Clerk 429 (rate limit) or 5xx, or a network blip.
            prepareError = e;
            log.info('[useAuthCardSubmit] prepareSecondFactor failed', { code: getClerkErrorCode(e) });
          }
        }
        setVerificationEmail(email);
        setView('two-factor');
        if (prepareError) {
          reportDashboardAuthOutcome('failed', 'second_factor', getClerkErrorCode(prepareError));
          setFormError(getClerkErrorMessage(prepareError));
        }
        return;
      }
      reportDashboardAuthOutcome('incomplete', 'sign_in');
    } catch (signInError) {
      const errorCode = getClerkErrorCode(signInError);
      log.info('[useAuthCardSubmit] signIn failed, error code:', { errorCode });

      // Account exists but was created via Google OAuth — password strategy is invalid
      if (errorCode === 'strategy_for_user_invalid') {
        reportDashboardAuthOutcome('failed', 'sign_in', errorCode);
        setFormError('This account uses Google sign-in. Use the "Continue with Google" button instead.');
        return;
      }

      // Step 2: Account doesn't exist — pivot to sign-up
      if (errorCode === 'form_identifier_not_found') {
        // TOS accepted implicitly by continuing (passive agreement text on form)
        try {
          if (!signUp) throw new Error('Sign-up is still loading.');
          const signUpResult: any = await signUp.create({
            emailAddress: email,
            password,
            legalAccepted: true,
          } as any);
          log.info('[useAuthCardSubmit] signUp.create result status:', { status: signUpResult.status });

          if (signUpResult.status === 'complete' && signUpResult.createdSessionId && setActive) {
            expectDashboardAuthCompletion(signUpResult.createdSessionId);
            await setActive({ session: signUpResult.createdSessionId });
            navigate(afterAuthUrl || DEFAULT_AFTER_AUTH_URL, { replace: true });
            return;
          } else if (signUpResult.status === 'missing_requirements') {
            const emailVerification = signUpResult.verifications?.emailAddress;
            if (emailVerification && emailVerification.status !== 'verified') {
              reportDashboardAuthOutcome('verification_required', 'email_verification');
              log.info('[useAuthCardSubmit] Email verification required by Clerk');
              await signUpResult.prepareEmailAddressVerification({ strategy: 'email_code' });
              setVerificationEmail(email);
              setView('verify-email');
              return;
            } else if (signUpResult.createdSessionId && setActive) {
              expectDashboardAuthCompletion(signUpResult.createdSessionId);
              await setActive({ session: signUpResult.createdSessionId });
              navigate(afterAuthUrl || DEFAULT_AFTER_AUTH_URL, { replace: true });
              return;
            }
          }
          reportDashboardAuthOutcome('incomplete', 'sign_up');
        } catch (signUpError) {
          if (isAlreadySignedInError(signUpError)) {
            reportDashboardAuthOutcome('incomplete', 'sign_in');
            navigate(afterAuthUrl || DEFAULT_AFTER_AUTH_URL, { replace: true });
            return;
          }
          reportDashboardAuthOutcome('failed', 'sign_up', getClerkErrorCode(signUpError));
          setFormError(getClerkErrorMessage(signUpError));
        }
      } else if (isAlreadySignedInError(signInError)) {
        reportDashboardAuthOutcome('incomplete', 'sign_in');
        log.info('[useAuthCardSubmit] Already signed in, redirecting');
        navigate(afterAuthUrl || DEFAULT_AFTER_AUTH_URL, { replace: true });
        return;
      } else {
        reportDashboardAuthOutcome('failed', 'sign_in', errorCode);
        setFormError(getClerkErrorMessage(signInError));
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [afterAuthUrl, signIn, signUp, setActive, navigate]);

  // Verify email code (sign-up flow)
  const handleVerifyEmail = useCallback(async (code: string) => {
    log.info('[useAuthCardSubmit] handleVerifyEmail');
    setFormError(null);
    reportDashboardAuthVerification('email_verification');

    try {
      setIsSubmitting(true);
      if (!signUp) throw new Error('Sign-up is still loading.');
      const result: any = await (signUp as any).attemptEmailAddressVerification({ code });
      log.info('[useAuthCardSubmit] attemptEmailAddressVerification result:', { status: result.status });
      if (result.status === 'complete' && result.createdSessionId && setActive) {
        expectDashboardAuthCompletion(result.createdSessionId);
        await setActive({ session: result.createdSessionId });
        navigate(afterAuthUrl || DEFAULT_AFTER_AUTH_URL, { replace: true });
        return;
      }
      reportDashboardAuthOutcome('incomplete', 'email_verification');
    } catch (error) {
      reportDashboardAuthOutcome('failed', 'email_verification', getClerkErrorCode(error));
      setFormError(getClerkErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }, [signUp, setActive, afterAuthUrl, navigate]);

  // Complete the email-code second factor (MFA / device verification)
  const handleSecondFactor = useCallback(async (code: string) => {
    log.info('[useAuthCardSubmit] handleSecondFactor');
    setFormError(null);
    reportDashboardAuthVerification('second_factor');

    try {
      setIsSubmitting(true);
      if (!signIn) throw new Error('Sign-in is still loading.');
      const result: any = await (signIn as any).attemptSecondFactor({ strategy: 'email_code', code });
      log.info('[useAuthCardSubmit] attemptSecondFactor result:', { status: result.status });
      if (result.status === 'complete' && result.createdSessionId && setActive) {
        expectDashboardAuthCompletion(result.createdSessionId);
        await setActive({ session: result.createdSessionId });
        navigate(afterAuthUrl || DEFAULT_AFTER_AUTH_URL, { replace: true });
        return;
      }
      reportDashboardAuthOutcome('incomplete', 'second_factor');
    } catch (error) {
      reportDashboardAuthOutcome('failed', 'second_factor', getClerkErrorCode(error));
      setFormError(getClerkErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }, [signIn, setActive, afterAuthUrl, navigate]);

  // Start forgot-password flow
  const handleForgotPassword = useCallback(async (email: string) => {
    log.info('[useAuthCardSubmit] handleForgotPassword');
    clearDashboardAuthAttempt();
    setFormError(null);

    try {
      setIsSubmitting(true);
      if (!signIn) throw new Error('Sign-in is still loading.');
      const result: any = await signIn.create({
        identifier: email,
        strategy: 'reset_password_email_code',
      } as any);

      const resetFactor = result.supportedFirstFactors?.find(
        (f: any) => f.strategy === 'reset_password_email_code'
      );
      if (resetFactor && result.prepareFirstFactor) {
        await result.prepareFirstFactor({
          emailAddressId: resetFactor.emailAddressId,
          strategy: 'reset_password_email_code',
        });
      }
      setView('reset-password');
    } catch (error) {
      setFormError(getClerkErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }, [signIn]);

  // Complete password reset
  const handleResetPassword = useCallback(async (code: string, newPassword: string) => {
    log.info('[useAuthCardSubmit] handleResetPassword');
    setFormError(null);

    try {
      setIsSubmitting(true);
      if (!signIn) throw new Error('Sign-in is still loading.');
      const result: any = await (signIn as any).attemptFirstFactor({
        code,
        password: newPassword,
        strategy: 'reset_password_email_code',
      });
      log.info('[useAuthCardSubmit] attemptFirstFactor result:', { status: result.status });
      if (result.status === 'complete' && result.createdSessionId && setActive) {
        expectDashboardAuthCompletion(result.createdSessionId);
        await setActive({ session: result.createdSessionId });
        navigate(afterAuthUrl || DEFAULT_AFTER_AUTH_URL, { replace: true });
        return;
      }
    } catch (error) {
      setFormError(getClerkErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }, [signIn, setActive, afterAuthUrl, navigate]);

  return {
    view,
    setView,
    formError,
    isSubmitting,
    isInteractionDisabled,
    // Exposed so AuthCard can tell "Clerk never hydrated" (clerk-js load
    // failure -> form dead forever) apart from an in-flight submit; the card
    // surfaces a retry notice after CLERK_LOAD_TIMEOUT_MS of not-ready.
    isCardReady,
    verificationEmail,
    handleGoogleAuth,
    handleEmailPasswordSubmit,
    handleVerifyEmail,
    handleSecondFactor,
    handleForgotPassword,
    handleResetPassword,
  };
}
